import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, CircularProgress, Paper, Pill, Stack, Typography} from 'views/components';
import {DescriptionOutlined as FormIcon} from 'views/icons';
import {translate} from 'views/module';

import {formatDateTime, personName} from '../format';

import backend from 'utils/backend';

// The answer rows keep the prompt and section the lead saw when they submitted, so a later form
// change does not rewrite what they answered.
const groupBySection = (answers) => {
  return answers.reduce((sections, answer) => {
    const section = sections.find((candidate) => candidate.name === answer.section);

    if (section) {
      section.answers.push(answer);
      return sections;
    }

    return [...sections, {name: answer.section, answers: [answer]}];
  }, []);
};

const Submission = (props) => {
  const {submission} = props;
  const sections = React.useMemo(() => groupBySection(submission.answers), [submission.answers]);
  const contact = [submission.email, submission.phoneNumber].filter(Boolean).join(' / ');

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          px: 2,
          py: 1,
          backgroundColor: 'grey.100',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <FormIcon sx={{mr: 1}} />
          <Typography variant='h6'>
            {translate('loomLead.LoomLeadDetail.IntakeForm.Title')}
          </Typography>
        </Box>
        <Pill
          label={translate('loomLead.LoomLeadDetail.IntakeForm.Submitted', {value: formatDateTime(submission.submittedAt)})}
          variant='neutral'
        />
      </Box>
      <Stack spacing={2} sx={{p: 2}}>
        <Stack spacing={0.5}>
          <Typography variant='subtitle1' sx={{fontWeight: 'bold'}}>
            {translate('loomLead.LoomLeadDetail.IntakeForm.SubmittedBy')}
          </Typography>
          <Typography variant='body2'>
            {personName(submission.submitter) || '-'}
          </Typography>
          {
            contact &&
            <Typography variant='body2' sx={{color: '#6B6760'}}>
              {contact}
            </Typography>
          }
        </Stack>
        {
          sections.map((section) => (
            <Stack key={section.name} spacing={1.5}>
              <Typography sx={{fontWeight: 600, fontSize: '11px', color: '#A8A49E', textTransform: 'uppercase'}}>
                {section.name.replace(/_/g, ' ')}
              </Typography>
              {
                section.answers.map((answer) => (
                  <Stack key={answer.questionCode} spacing={0.5}>
                    <Typography variant='subtitle1' sx={{fontWeight: 'bold'}}>
                      {answer.prompt}
                    </Typography>
                    {
                      answer.values.length > 1 ?
                        <Stack direction='row' sx={{gap: '7px', flexWrap: 'wrap'}}>
                          {
                            answer.values.map((value) => (
                              <Pill key={value} label={value} variant='neutral' />
                            ))
                          }
                        </Stack> :
                        <Typography variant='body2' sx={{whiteSpace: 'pre-wrap'}}>
                          {answer.values[0] || '-'}
                        </Typography>
                    }
                  </Stack>
                ))
              }
            </Stack>
          ))
        }
      </Stack>
    </Paper>
  );
};
Submission.propTypes = {
  submission: PropTypes.object.isRequired,
};

const IntakeForm = (props) => {
  const {clientId, refresh} = props;

  const [submissions, setSubmissions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isCancelled = false;

    const loadSubmissions = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await backend.loom.loomLead.workflow.listIntakeSubmissionsForClient(clientId);
        if (!isCancelled) setSubmissions(result);
      } catch (loadError) {
        console.error(loadError);
        if (isCancelled) return;
        setSubmissions([]);
        setError(translate('loomLead.LoomLeadDetail.IntakeForm.LoadError'));
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    loadSubmissions();

    return () => {
      isCancelled = true;
    };
  }, [clientId, refresh]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{p: 2}}>
      {
        error &&
        <Alert severity='error'>{error}</Alert>
      }
      {
        !error && submissions.length === 0 &&
        <Alert severity='info'>{translate('loomLead.LoomLeadDetail.IntakeForm.Empty')}</Alert>
      }
      {
        submissions.map((submission) => (
          <Submission
            key={submission.id}
            submission={submission}
          />
        ))
      }
    </Stack>
  );
};
IntakeForm.propTypes = {
  clientId: PropTypes.string.isRequired,
  refresh: PropTypes.number.isRequired,
};

export default React.memo(IntakeForm);
